<template>
  <div class="hidden" />
</template>

<script>
import _ from 'lodash'
import { RestApiClient } from 'ifield-core-components'
import NdjsonClient from '@/lib/NdjsonClient.js'
import FlowlineDataClient from '@/js/analyze/FlowlineDataClient.js'
import { groupData } from '@/js/analyze/GroupData.js'
import HeatmapRenderer from '@/js/map-renderer/HeatmapRenderer.js'
import MeshRenderer from '@/js/map-renderer/MeshRenderer.js'
import PlotRenderer from '@/js/map-renderer/PlotRenderer.js'
import FlowlineRenderer from '@/js/map-renderer/FlowlineRenderer.js'
import ColorUtil from '@/js/util/ColorUtil.js'
import { HIGHLIGHT_SCALE_FACTOR } from '@/constants.js'
import VARIABLES from '@/assets/scss/variables.module.scss'
import { toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

/**
 * 動線強調マップ用データの取得後後処理
 * * プレイス座標を各データに割当
 * * 時間を分単位に変換
 * * ユーザー毎グルーピング
 * @param {Object} data API取得データ
 * @returns 後処理後データ
 */
const processFlowlineData = (data) => {
  const placeMap = new Map()
  for (const p of data.places) {
    placeMap.set(p.id, [p.lng, p.lat])
  }

  let stays = data.stays.map((d) => ({
    user_id: d.user_id,
    coordinates: placeMap.get(d.place_id),
    duration: d.seconds,
  }))
  let flows = data.flows.map((d) => ({
    user_id: d.user_id,
    origin_coordinates: placeMap.get(d.origin_place_id),
    dest_coordinates: placeMap.get(d.dest_place_id),
    count: d.count,
  }))
  stays = groupData(stays, 'user_id')
  flows = groupData(flows, 'user_id')

  const allUserIds = [
    ...new Set([
      ...stays.map((d) => d.user_id),
      ...flows.map((d) => d.user_id),
    ]),
  ]
  return allUserIds.map((id) => {
    const stay = stays.find((s) => s.user_id === id)
    const flow = flows.find((f) => f.user_id === id)
    return {
      user_id: id,
      stays: stay ? stay.data : [],
      flows: flow ? flow.data : [],
    }
  })
}

/**
 * 指定キーの表示状態取得
 * @param {Boolean|Object} visible 表示状態
 * @param {String} key キー
 * @returns {Boolean} 表示状態
 */
const getVisibleByKey = (visible, key) => {
  return typeof visible === 'boolean' ? visible : visible[key]
}

// レイヤー種類
const LAYER_TYPE = {
  HEATMAP: HeatmapRenderer.LAYER_ID,
  MESH: MeshRenderer.LAYER_ID,
  PLOT: PlotRenderer.LAYER_ID,
  FLOWLINE: FlowlineRenderer.LAYER_ID,
}

/**
 * レンダラーのfactoryインスタンス保持
 */
const rendererFactory = {
  [LAYER_TYPE.HEATMAP]: new HeatmapRenderer(),
  [LAYER_TYPE.MESH]: new MeshRenderer(),
  [LAYER_TYPE.PLOT]: new PlotRenderer(),
  [LAYER_TYPE.FLOWLINE]: new FlowlineRenderer(),
}

// データクライアント
const client = {
  location: new NdjsonClient(),
  flowline: new FlowlineDataClient({ postProcess: processFlowlineData }),
}

// 使用30色
const COLORS = [
  VARIABLES.ENTITY_COLOR_RED,
  VARIABLES.ENTITY_COLOR_NAVY,
  VARIABLES.ENTITY_COLOR_ORANGE,
  VARIABLES.ENTITY_COLOR_GREEN,
  VARIABLES.ENTITY_COLOR_WINE_RED,
  VARIABLES.ENTITY_COLOR_BLUE,
  VARIABLES.ENTITY_COLOR_RED_PURPLE,
  VARIABLES.ENTITY_COLOR_LIGHT_GREEN,
  VARIABLES.ENTITY_COLOR_GOLD,
  VARIABLES.ENTITY_COLOR_PURPLE,
  VARIABLES.ENTITY_COLOR_PEACH_PINK,
  VARIABLES.ENTITY_COLOR_BLUE_GREEN,
  VARIABLES.ENTITY_COLOR_BROWN,
  VARIABLES.ENTITY_COLOR_LIGHT_BLUE,
  VARIABLES.ENTITY_COLOR_ORANGE_RED,
  VARIABLES.ENTITY_COLOR_DEEP_BLUE_GREEN,
  VARIABLES.ENTITY_COLOR_DEEP_PINK,
  VARIABLES.ENTITY_COLOR_LIME_GREEN,
  VARIABLES.ENTITY_COLOR_YELLOW,
  VARIABLES.ENTITY_COLOR_BLUE_PURPLE,
  VARIABLES.ENTITY_COLOR_PINK,
  VARIABLES.ENTITY_COLOR_FRESH_GREEN,
  VARIABLES.ENTITY_COLOR_WISTERIA_PURPLE,
  VARIABLES.ENTITY_COLOR_EMERALD,
  VARIABLES.ENTITY_COLOR_MANDARIN_ORANGE,
  VARIABLES.ENTITY_COLOR_GOLDEN_YELLOW,
  VARIABLES.ENTITY_COLOR_ROSE_PINK,
  VARIABLES.ENTITY_COLOR_SKY_BLUE,
  VARIABLES.ENTITY_COLOR_PASSION_RED,
  VARIABLES.ENTITY_COLOR_ROYAL_BLUE,
]

export default {
  name: 'AppAnalyzeDataLayerManager',
  inheritAttrs: false,
  props: {
    /**
     * 開始日時
     */
    from: {
      type: String,
      default: null,
    },
    /**
     * 終了日時
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * 対象ユーザー
     */
    users: {
      type: Array,
      default: null,
    },
    /**
     * フロアID
     */
    indoorLevelId: {
      type: Number,
      default: null,
    },
    /**
     * レイヤー種類
     */
    layerType: {
      type: String,
      default: LAYER_TYPE.HEATMAP,
    },
    /**
     * レイヤー表示状態
     */
    visible: {
      type: Object,
      default: () => ({ all: true, geometry: true, user: true }),
    },
    /**
     * ハイライト対象のユーザーID
     */
    highlightedUserId: {
      type: Number,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // 動線強調マップ用パラメータ
      flowline: {
        radiusRange: [8, 16, 24, 32, 40],
        widthRange: [2, 3, 4, 5, 6],
        geometryVisible: {
          staycircle: true,
          flowline: true,
        },
      },
      // 凡例用情報
      legend: {
        colorRange: null,
        threshold: null,
        valueRange: null,
        radiusDomain: null,
        widthDomain: null,
      },
      // データが空の場合にtrue。UI起因の不要な再描画を抑制するため
      isDataEmpty: false,
    }
  },
  computed: {
    /**
     * 対象ユーザーID
     * @type {Array.<Number>}
     */
    userIds: function () {
      if (!this.users || !this.users.length) return null
      return this.users.map((u) => u.id)
    },
    /**
     * データクライアント
     * @type {Object}
     */
    client: function () {
      return this.layerType === LAYER_TYPE.FLOWLINE
        ? client.flowline
        : client.location
    },
    /**
     * APIクライアント用パラメータ
     * @type {Object}
     */
    paramsForClient: function () {
      return {
        from: this.from,
        to: this.to,
        userId: this.userIds,
        indoorLevelId: this.indoorLevelId,
      }
    },
    /**
     * 表示状態の値
     * @type {Boolean}
     */
    visibleValue: function () {
      if (Array.isArray(this.visible)) {
        return this.visible.length ? this.visible[0].visible : true
      } else {
        return this.visible
      }
    },
    /**
     * 使用10色(RGB)
     * @type {Array.<Array.<Number>>}
     */
    rgbColors: function () {
      return COLORS.map((c) => ColorUtil.hexToRgb(c))
    },
    /**
     * ユーザーのindexMap
     * @type {Map}
     */
    userIndexMap: function () {
      if (!this.userIds) return null
      return new Map(this.users.map(({ id }, index) => [id, index]))
    },
    /**
     * visibleなユーザーIDのSet
     * @type {Set}
     */
    visibleUserIdSet: function () {
      let visibleIds = []
      if (this.userIds) {
        visibleIds = this.userIds.filter((id) =>
          getVisibleByKey(this.visible.user, `${id}`)
        )
      }
      return new Set(visibleIds)
    },
    /**
     * 加工済みユーザーデータ
     * - 色の割り当て
     * @type {Array.<Object>}
     */
    usersModified: function () {
      if (!this.userIndexMap) return []
      return this.users.map((user) => ({
        ...user,
        color: this.$_getUserColorById(user.id),
      }))
    },
    /**
     * deck.glで色を取得するAccessor
     * @type {Function}
     */
    getColor: function () {
      return (d) => this.$_getUserColorById(d.user_id) || d.color
    },
    /**
     * メッシュのセルサイズ(m)
     * @type {Number}
     */
    meshCellSize: function () {
      return this.indoorLevelId ? 1 : 10
    },
    /**
     * レイヤーメニュー用データ
     * @type {Object}
     */
    layerMenuData: function () {
      return {
        id: this.layerType,
        visible: this.visible.all,
        name: this.$t(`appAnalyzeDataLayerManager.layerName.${this.layerType}`),
      }
    },
    /**
     * レイヤー選択用選択肢データ
     * @type {Array.<Object>}
     */
    layerOptions: function () {
      return [
        {
          value: LAYER_TYPE.HEATMAP,
          text: this.$t('appAnalyzeDataLayerManager.layerName.heatmap'),
        },
        {
          value: LAYER_TYPE.MESH,
          text: this.$t('appAnalyzeDataLayerManager.layerName.mesh'),
        },
        {
          value: LAYER_TYPE.PLOT,
          text: this.$t('appAnalyzeDataLayerManager.layerName.plot'),
        },
        {
          value: LAYER_TYPE.FLOWLINE,
          text: this.$t('appAnalyzeDataLayerManager.layerName.flowline'),
        },
      ]
    },
    /**
     * 半径mapping
     * @type {Array.<Object>}
     */
    radiuses: function () {
      if (!this.legend.radiusDomain) return []
      let result = []
      for (let i = 0; i < this.legend.radiusDomain.length; ++i) {
        result.push({
          radius: this.flowline.radiusRange[i],
          value: this.legend.radiusDomain[i],
        })
      }
      return result
    },
    /**
     * 線幅mapping
     * @type {Array.<Object>}
     */
    widths: function () {
      if (!this.legend.widthDomain) return []
      let result = []
      for (let i = 0; i < this.legend.widthDomain.length; ++i) {
        result.push({
          width: this.flowline.widthRange[i],
          value: this.legend.widthDomain[i],
        })
      }
      return result
    },
    /**
     * 凡例用情報
     * @type {Object}
     */
    legendData: function () {
      switch (this.layerType) {
        case LAYER_TYPE.HEATMAP:
          return {
            colorRange: this.legend.colorRange,
            threshold: this.legend.threshold,
          }
        case LAYER_TYPE.MESH:
          return {
            colorRange: this.legend.colorRange,
            valueRange: this.legend.valueRange,
          }
        case LAYER_TYPE.PLOT:
          return {
            users: this.usersModified,
          }
        case LAYER_TYPE.FLOWLINE:
          return {
            users: this.usersModified,
            radiuses: this.radiuses,
            widths: this.widths,
          }
        default:
          return {}
      }
    },
  },
  mounted: function () {
    this.$_execute()
  },
  beforeDestroy: function () {
    // リクエスト中断
    this.client.abort()
  },
  watch: {
    paramsForClient: {
      handler: async function () {
        await this.client.abort()
        this.$_createRenderer()
        this.$_updateLegendInfo()
        this.$_emitLayerMenuDataChanged()
      },
      deep: true,
    },
    layerType: function () {
      this.$_createRenderer()
      this.$_updateLegendInfo()
    },
    'visible.all': function () {
      this.$_createRenderer({ onDataLoad: () => this.$_hideLoading() })
    },
    'visible.user': function () {
      // データが空の場合はユーザー単体の表示変更による再描画は不要
      if (this.isDataEmpty) return
      this.$_createRenderer({ onDataLoad: () => this.$_hideLoading() })
    },
    'visible.geometry': function () {
      if (this.layerType !== LAYER_TYPE.FLOWLINE) {
        this.$_createRenderer({ onDataLoad: () => this.$_hideLoading() })
        return
      }
      this.$_updateFlowlineGeometryVisible()
    },
    highlightedUserId: function () {
      // データが空の場合はハイライト処理による再描画は不要
      if (this.isDataEmpty) return
      this.$_createRenderer({ onDataLoad: () => this.$_hideLoading() })
    },
    layerMenuData: function () {
      this.$_emitLayerMenuDataChanged()
    },
    legendData: {
      handler: function () {
        this.$_emitLegendDataChanged()
      },
      deep: true,
    },
  },
  methods: {
    /**
     * ユーザーの表示状態に応じて色と不透明度を返す
     * @param {Object} d データオブジェクト
     * @returns {Array.<Number>} 色 ([r, g, b, a])
     */
    $_getColorWithOpacity(d) {
      const color = this.$_getUserColorById(d.user_id) || d.color
      if (!color) return [0, 0, 0, 0] // Fallback
      // プロットのみ不透明度を10%に設定
      const alpha = this.visibleUserIdSet.has(d.user_id) ? 255 : 255 * 0.1
      return [color[0], color[1], color[2], alpha]
    },

    /**
     * 分析実行
     */
    $_execute: function () {
      this.$_createRenderer()
      this.$_updateLegendInfo()
      this.$_emitLegendDataChanged()
      this.$_emitLayerOptions()
      this.$_emitLayerMenuDataChanged()
    },

    /**
     * ロケーションデータ取得
     * @yields {Array.<Object>} 統計用ロケーションデータ
     */
    $_getLocations: async function* () {
      const url = RestApiClient.createApiUrl('location/report')
      this.$_addQueryParameters(url)

      try {
        yield* this.client.getData(url)
      } catch (error) {
        this.handleApiError(error)
        return
      }
    },

    /**
     * 動線強調マップデータ取得
     */
    $_getFlowlineData: async function () {
      this.$_showLoading()
      const url = RestApiClient.createApiUrl('location/flowline')
      this.$_addQueryParameters(url)

      try {
        return await this.client.getData(url)
      } catch (error) {
        this.handleApiError(error)
        return
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * パラメータを追加する
     * @param {URL} url URL
     */
    $_addQueryParameters: function (url) {
      if (this.from) {
        url.searchParams.append('from', toServerTz(this.from))
      }
      if (this.to) {
        url.searchParams.append('to', toServerTz(this.to))
      }
      if (this.userIds) {
        const userId = Array.isArray(this.userIds)
          ? this.userIds.join(',')
          : this.userIds
        url.searchParams.append('user_id', userId)
      }
      if (this.indoorLevelId) {
        url.searchParams.append('indoor_level_id', this.indoorLevelId)
      }
    },

    /**
     * ユーザーIDから色を取得する
     * @param {Number}
     * @returns {Array.<Number>} 色 ([r, g, b])
     */
    $_getUserColorById: function (id) {
      if (!this.userIndexMap) return null
      return this.rgbColors[this.userIndexMap.get(id) % this.rgbColors.length]
    },

    /**
     * レンダラー作成
     * @param {Object} overrideProps 上書きするprops
     * @returns {Object} レンダラー
     */
    $_createRenderer: async function (overrideProps = {}) {
      const dataSource =
        this.layerType === LAYER_TYPE.FLOWLINE
          ? this.$_getFlowlineData
          : this.$_getLocations
      const props = {
        visible: this.visible.all,
        highlightedUserId: this.highlightedUserId,
        getPosition: (d) => [d.lng, d.lat],
        onDataLoad: (data) => {
          if (!data) return // エラーはハンドリング済み
          this.isDataEmpty = !data.length && !this.$alert.isVisible()
          if (this.isDataEmpty) this.$_emitNoLocationFound()
          this.$_hideLoading()
        },
        ...overrideProps,
      }

      let renderer = null
      switch (this.layerType) {
        case LAYER_TYPE.HEATMAP:
          renderer = this.$_createHeatmapRenderer(dataSource, props)
          break
        case LAYER_TYPE.MESH:
          renderer = this.$_createMeshRenderer(dataSource, props)
          break
        case LAYER_TYPE.PLOT:
          renderer = this.$_createPlotRenderer(dataSource, props)
          break
        case LAYER_TYPE.FLOWLINE:
          renderer = this.$_createFlowlineRenderer(dataSource, props)
          break
        default:
          break
      }

      this.$_emitRendererChangedEvent(renderer)
      this.$_showLoading()
    },

    /**
     * ヒートマップのレンダラー作成
     * @param {Function|Array.<Object>} dataSource データソース
     * @param {Object} props 追加プロパティ
     * @returns {Object} ヒートマップのレンダラーオブジェクト
     */
    $_createHeatmapRenderer: function (dataSource, props) {
      const factory = rendererFactory[LAYER_TYPE.HEATMAP]
      return factory.createRenderer(dataSource, props)
    },

    /**
     * メッシュマップのレンダラー作成
     * @param {Function|Array.<Object>} dataSource データソース
     * @param {Object} props 追加プロパティ
     * @returns {Object} ヒートマップのレンダラーオブジェクト
     */
    $_createMeshRenderer: function (dataSource, props) {
      const factory = rendererFactory[LAYER_TYPE.MESH]
      return factory.createRenderer(dataSource, {
        ...props,
        cellSize: this.meshCellSize,
        onSetColorDomain: ([min, max]) => {
          this.legend.valueRange = { min, max }
        }, // 凡例用に集計結果を取得
      })
    },

    /**
     * プロットのレンダラー作成
     * @param {Function|Array.<Object>} dataSource データソース
     * @param {Object} props 追加プロパティ
     * @returns {Object} ヒートマップのレンダラーオブジェクト
     */
    $_createPlotRenderer: function (dataSource, props) {
      const factory = rendererFactory[LAYER_TYPE.PLOT]
      const baseRadius = 3

      // サブレイヤーを通常表示(others)とハイライト表示(highlighted)に分ける
      const subLayerProps = {
        others: {
          getFilterValue: (d) =>
            // 非ハイライト対象
            d.user_id !== this.highlightedUserId ? 1 : 0,
          filterRange: [0.5, 1.5],
          getRadius: baseRadius,
          updateTriggers: {
            getFilterValue: [this.highlightedUserId],
            getFillColor: [this.visibleUserIdSet],
          },
        },
        highlighted: {
          getFilterValue: (d) =>
            // ハイライト対象
            d.user_id === this.highlightedUserId ? 1 : 0,
          filterRange: [0.5, 1.5],
          getRadius: baseRadius * HIGHLIGHT_SCALE_FACTOR, // 半径を大きくする
          updateTriggers: {
            getFilterValue: [this.highlightedUserId],
            getFillColor: [this.visibleUserIdSet],
          },
        },
      }

      return factory.createRenderer(dataSource, {
        ...props,
        getFillColor: this.$_getColorWithOpacity,
        _subLayerProps: subLayerProps,
      })
    },

    /**
     * 動線強調マップのレンダラー作成
     * @param {Function|Array.<Object>} dataSource データソース
     * @param {Object} props 追加プロパティ
     * @returns {Object} 動線強調マップのレンダラーオブジェクト
     */
    $_createFlowlineRenderer: function (dataSource, props) {
      const factory = rendererFactory[LAYER_TYPE.FLOWLINE]
      return factory.createRenderer(dataSource, {
        ...props,
        groupKey: 'user_id',
        radiusRange: this.flowline.radiusRange,
        widthRange: this.flowline.widthRange,
        onSetRadiusDomain: (domain) => {
          this.legend.radiusDomain = domain
        },
        onSetWidthDomain: (domain) => {
          this.legend.widthDomain = domain
        },
        highlightedUserId: this.highlightedUserId,
        _subLayerProps: {
          staycircle: {
            visible: props.visible && this.flowline.geometryVisible.staycircle,
            getPosition: (d) => d.coordinates,
            getFillColor: this.$_getColorWithOpacity,
            // visibleUserIdSetの変更をトリガーに色を再評価させる
            updateTriggers: {
              getFillColor: [this.visibleUserIdSet],
            },
          },
          flowline: {
            visible: props.visible && this.flowline.geometryVisible.flowline,
            getSourcePosition: (d) => d.origin_coordinates,
            getTargetPosition: (d) => d.dest_coordinates,
            getColor: this.$_getColorWithOpacity,
            // visibleUserIdSetの変更をトリガーに色を再評価させる
            updateTriggers: {
              getColor: [this.visibleUserIdSet],
            },
          },
        },
      })
    },

    /**
     * 凡例表示用情報を取得する
     */
    $_updateLegendInfo: function () {
      const factory = rendererFactory[this.layerType]
      if (this.layerType === LAYER_TYPE.HEATMAP) {
        this.legend.colorRange = factory.colorRange
        this.legend.threshold = factory.threshold
      } else if (this.layerType === LAYER_TYPE.MESH) {
        this.legend.colorRange = factory.colorRange
      }
    },

    /**
     * 図形種類の表示状態更新処理
     */
    $_updateFlowlineGeometryVisible: function () {
      const newVisible = {
        ...this.flowline.geometryVisible,
        staycircle: getVisibleByKey(this.visible.geometry, 'staycircle'),
        flowline: getVisibleByKey(this.visible.geometry, 'flowline'),
      }
      // 全てfalseだったら変更前falseだった方をtrueに変換
      if (Object.values(newVisible).every((v) => !v)) {
        for (const [key, value] of Object.entries(
          this.flowline.geometryVisible
        )) {
          if (!value) {
            newVisible[key] = true
          }
        }
        /**
         * ジオメトリ表示状態変更イベント
         * @param {Object} visible 各図形種類の表示状態
         */
        this.$emit('geometry-visible-changed', newVisible)
        return
      }

      if (!_.isEqual(this.flowline.geometryVisible, newVisible)) {
        this.flowline.geometryVisible = newVisible
        this.$_createRenderer({ onDataLoad: () => this.$_hideLoading() })
      }
    },

    /**
     * レンダラー変更イベントを発行する
     */
    $_emitRendererChangedEvent: function (renderer) {
      /**
       * レンダラー変更イベント
       * @param {Object} renderer 分析レイヤーのレンダラー
       */
      this.$emit('renderer-changed', renderer)
    },

    /**
     * レイヤーメニューデータ変更イベントを発行する
     */
    $_emitLayerMenuDataChanged: function () {
      /**
       * レイヤーメニューデータ変更イベント
       * @param {Array.<Object>} layerMenuData レイヤーメニューデータ
       */
      this.$emit('layer-menu-data-changed', [this.layerMenuData])
    },

    /**
     * 凡例データ変更イベント発行
     */
    $_emitLegendDataChanged: function () {
      /**
       * 凡例データ変更イベント
       * @param {Object} legendData 凡例データ
       */
      this.$emit('legend-data-changed', this.legendData)
    },

    /**
     * レイヤー選択肢イベントを発行する
     */
    $_emitLayerOptions: function () {
      /**
       * レイヤー選択肢イベント
       * @param {Array.<Object>} layerOptions レイヤー選択肢
       */
      this.$emit('layer-options-created', this.layerOptions)
    },

    /**
     * ロケーションデータが無いイベントを発行
     */
    $_emitNoLocationFound: function () {
      /**
       * ロケーションデータが無いイベント
       */
      this.$emit('no-location-found')
    },

    /**
     * ローディング表示
     */
    $_showLoading: function () {
      this.$_emitIsLoadingChanged(true)
    },

    /**
     * ローディング非表示
     */
    $_hideLoading: function () {
      this.$_emitIsLoadingChanged(false)
    },

    /**
     * ローディング状態変更イベント発行
     * @param {Boolean} isLoading ロード中の場合はtrue
     */
    $_emitIsLoadingChanged: function (isLoading) {
      /**
       * ローディング状態変更イベント
       * @param {Boolean} isLoading ロード中の場合はtrue
       */
      this.$emit('is-loading-changed', isLoading)
    },
  },
}
</script>

<style lang="scss" scoped>
.hidden {
  display: none;
}
</style>
